import type {
  IntervalUnit,
  MessagePriority,
  MessageType,
  NotificationChannel,
  NotificationTestFormValues,
  RecipientType,
  ScheduleType,
} from './types/notificationTest';

export const RECIPIENT_TYPE_CHOICES: Array<{
  id: RecipientType;
  labelKey: string;
}> = [
  { id: 'INDIVIDUAL', labelKey: 'resources.test.choices.recipientType.INDIVIDUAL' },
  { id: 'GROUP', labelKey: 'resources.test.choices.recipientType.GROUP' },
];

export const MESSAGE_TYPE_CHOICES: Array<{
  id: MessageType;
  labelKey: string;
}> = [
  { id: 'TEMPLATE', labelKey: 'resources.test.choices.messageType.TEMPLATE' },
  { id: 'CUSTOM', labelKey: 'resources.test.choices.messageType.CUSTOM' },
];

export const MESSAGE_PRIORITY_CHOICES: Array<{
  id: MessagePriority;
  labelKey: string;
}> = [
  { id: 'CRITICAL', labelKey: 'resources.test.choices.messagePriority.CRITICAL' },
  { id: 'STANDARD', labelKey: 'resources.test.choices.messagePriority.STANDARD' },
];

export const SCHEDULE_TYPE_CHOICES: Array<{
  id: ScheduleType;
  labelKey: string;
}> = [
  { id: 'NONE', labelKey: 'resources.test.choices.scheduleType.NONE' },
  { id: 'ONCE', labelKey: 'resources.test.choices.scheduleType.ONCE' },
  { id: 'RECURRING', labelKey: 'resources.test.choices.scheduleType.RECURRING' },
];

export const INTERVAL_UNIT_CHOICES: Array<{
  id: IntervalUnit;
  labelKey: string;
}> = [
  { id: 'MINUTE', labelKey: 'resources.test.choices.intervalUnit.MINUTE' },
  { id: 'HOUR', labelKey: 'resources.test.choices.intervalUnit.HOUR' },
  { id: 'DAY', labelKey: 'resources.test.choices.intervalUnit.DAY' },
  { id: 'WEEK', labelKey: 'resources.test.choices.intervalUnit.WEEK' },
  { id: 'MONTH', labelKey: 'resources.test.choices.intervalUnit.MONTH' },
  { id: 'YEAR', labelKey: 'resources.test.choices.intervalUnit.YEAR' },
];

export const CHANNEL_BACKEND_NAME: Record<NotificationChannel, string> = {
  email: 'Email',
  sms: 'SMS',
  whatsapp: 'WhatsApp',
  telegram: 'Telegram',
  'web-push': 'WebPush',
  'app-push': 'AppPush',
};

export const CHANNEL_ENDPOINT: Record<NotificationChannel, string> = {
  email: 'email',
  sms: 'sms',
  whatsapp: 'whatsapp',
  telegram: 'telegram',
  'web-push': 'web-push',
  'app-push': 'app-push',
};

export const CHANNEL_RECIPIENT_FIELD: Record<
  NotificationChannel,
  'addresses' | 'numbers' | 'chatIds'
> = {
  email: 'addresses',
  sms: 'numbers',
  whatsapp: 'numbers',
  telegram: 'chatIds',
  'web-push': 'addresses',
  'app-push': 'addresses',
};

export const DEFAULT_TIMEZONE = 'Europe/Madrid';

export const PROVIDER_TYPE = {
  TwilioWhatsapp: 4,
  MetaWhatsapp: 7,
} as const;

export const WHATSAPP_MEDIA_MAX_BYTES: Record<
  'IMAGE' | 'DOCUMENT' | 'VIDEO' | 'AUDIO',
  number
> = {
  IMAGE: 5 * 1024 * 1024,
  DOCUMENT: 100 * 1024 * 1024,
  VIDEO: 16 * 1024 * 1024,
  AUDIO: 16 * 1024 * 1024,
};

export const WHATSAPP_MEDIA_ACCEPT: Record<
  'IMAGE' | 'DOCUMENT' | 'VIDEO' | 'AUDIO',
  string
> = {
  IMAGE: 'image/jpeg,image/jpg,image/png',
  DOCUMENT: 'application/pdf,text/plain',
  VIDEO: 'video/mp4,video/3gpp',
  AUDIO: 'audio/aac,audio/mp4,audio/mpeg,audio/amr,audio/ogg',
};

export const createEmptyTestFormValues = (): NotificationTestFormValues => ({
  accountId: '',
  appId: '',
  messagePriority: 'STANDARD',
  webhookUrl: '',
  recipients: {
    type: 'INDIVIDUAL',
    addresses: [{ value: '' }],
    groupId: '',
  },
  message: {
    type: 'CUSTOM',
    subject: '',
    content: '',
    templateId: '',
    metaTemplateId: '',
    templateType: '',
    language: '',
    variables: [{ key: '', value: '' }],
    headerTextVariables: [],
    headerMedia: {
      type: '',
      source: 'URL',
      link: '',
      fileName: '',
      fileBase64: '',
      mimeType: '',
    },
    customMedia: {
      type: '',
      source: 'URL',
      link: '',
      fileName: '',
      fileBase64: '',
      mimeType: '',
    },
  },
  schedule: {
    type: 'NONE',
    date: null,
    time: '',
    recurring: {
      intervalValue: 0,
      intervalUnit: '',
      expiresDate: null,
      expiresTime: '',
    },
    timezone: DEFAULT_TIMEZONE,
  },
  attachments: [],
});

export const COMMON_TIMEZONES: string[] = [
  'Europe/Madrid',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Lisbon',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Mexico_City',
  'America/Sao_Paulo',
  'Asia/Kolkata',
  'Asia/Dubai',
  'Asia/Shanghai',
  'Asia/Tokyo',
  'Asia/Singapore',
  'Australia/Sydney',
  'UTC',
];
